import React from 'react';
import {
  Bar,
  XAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  YAxis,
  BarChart,
  ComposedChart,
  Line,
  Rectangle,
  ReferenceLine
} from 'recharts';
import { ReloadIconButton, MedicalSupplySelectModal, DiseaseInfo } from '../../elements';
import { TrendDataInfo, MedicalSupplyViewModel, AnimalAgeTrendDataInfo } from '../../models/injection';
import { AnimalType } from '../../models/animal';
import { extractApiErrorMessage, notifications, colors } from '../../utils';
import { InjectionsService } from '../../hooks/InjectionsService';
import { ApiService } from '../../hooks';
import { HealthSystemDomainDiseasesDiseaseViewModel } from '../../api/HealthSystemApi';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AnimalTypeTabs } from '../VaccinationAndMedication/component';
import { Card, CardHeader, CardContent } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '../../elements/ui/breadcrumb';
import { ToggleGroup, ToggleGroupItem } from '../../elements/ui/toggle-group.tsx';
import { ChartContainer, ChartLegend, ChartLegendContent, type ChartConfig } from '../../elements/ui/chart';
import { getISOWeekNumber } from '../../utils/date-range';

const getDefaultWeekRange = () => {
  const now = new Date();
  const currentWeek = getISOWeekNumber(now);
  return { start: Math.max(1, currentWeek - 10), end: currentWeek };
};

export interface VaccinationAndMedicationTrendDataProps {
  animalType: AnimalType;
  year?: number;
  weekStart?: number;
  weekEnd?: number;
  handleTabChange: (event: React.ChangeEvent<any>, newValue: AnimalType) => void;
}

function TrendTooltip({ active, payload, label, t, multiYear }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  const totalAnimals = data?.animalCount ?? 0;
  const weekLabel =
    multiYear && data?.year ? `Week ${data.weekNumber} (${data.year})` : `Week ${data?.weekNumber ?? label}`;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{weekLabel}</p>
      {payload
        .filter((entry: any) => entry.name?.trim())
        .map((entry: any) => {
          const displayValue = entry.dataKey === 'liveCount' ? totalAnimals : entry.value;
          return (
            <div key={entry.name} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.fill || entry.color }} />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">{displayValue}</span>
              {entry.dataKey === 'diedAnimalCount' && totalAnimals > 0 && (
                <span className="text-gray-500">({((entry.value / totalAnimals) * 100).toFixed(1)}%)</span>
              )}
            </div>
          );
        })}
      {data?.averageAge != null && (
        <div className="flex items-center gap-1.5 mt-1 pt-1 border-t border-gray-100">
          <span className="text-gray-600">{t('f-com-avg-age-days')}:</span>
          <span className="font-medium text-gray-900">{Math.round(data.averageAge)}</span>
        </div>
      )}
    </div>
  );
}

function AgeTooltip({ active, payload, label, grouping }: any) {
  if (!active || !payload?.length) return null;
  const unit = grouping === 'weeks' ? 'weeks' : 'days';
  const data = payload[0]?.payload;
  const totalAnimals = data?.totalCount ?? 0;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">
        {label} {unit}
      </p>
      {payload
        .filter((entry: any) => entry.name?.trim())
        .map((entry: any) => {
          const displayValue = entry.dataKey === 'count' ? totalAnimals : entry.value;
          return (
            <div key={entry.name} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.fill || entry.color }} />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">{displayValue}</span>
              {entry.dataKey === 'diedCount' && totalAnimals > 0 && (
                <span className="text-gray-500">({((entry.value / totalAnimals) * 100).toFixed(1)}%)</span>
              )}
            </div>
          );
        })}
    </div>
  );
}

function TrendXTick({ x, y, payload, data, multiYear }: any) {
  const dataIndex: number = data?.findIndex((d: any) => d.xLabel === payload.value) ?? -1;
  const item = dataIndex >= 0 ? data[dataIndex] : null;
  const weekNum = item?.weekNumber ?? payload.value;
  const isNewYear = multiYear && item?.year != null && (dataIndex === 0 || data[dataIndex - 1]?.year !== item.year);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fontSize={11} fill="#6b7280" dy={14}>
        {weekNum}
      </text>
      {isNewYear && (
        <text textAnchor="middle" fontSize={10} fill="#9ca3af" dy={27}>
          {item.year}
        </text>
      )}
    </g>
  );
}

export default function VaccinationAndMedicationTrendData({
  animalType,
  year: yearProp,
  weekStart: weekStartProp,
  weekEnd: weekEndProp,
  handleTabChange
}: VaccinationAndMedicationTrendDataProps) {
  const { t } = useTranslation();
  const injectionService = new InjectionsService();
  const apiService = new ApiService();

  const isSowOrGilt: boolean = animalType === AnimalType.Sow || animalType === AnimalType.Gilt;

  const currentYear = new Date().getFullYear();
  const currentWeek = getISOWeekNumber(new Date());
  const [yearStart, setYearStart] = React.useState<number>(yearProp ?? currentYear);
  const [yearEnd, setYearEnd] = React.useState<number>(yearProp ?? currentYear);
  const isMultiYear = yearStart !== yearEnd;

  const allWeeks = React.useMemo(() => Array.from({ length: 53 }, (_, i) => i + 1), []);
  const weekMenuProps = {
    MenuProps: {
      disableScrollLock: true,
      getContentAnchorEl: null,
      PaperProps: { style: { maxHeight: 40 * 6.5, overflowY: 'auto' as const } }
    }
  };

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [trendData, setTrendData] = React.useState([] as TrendDataInfo[]);
  const [showDiedAnimals, setShowDiedAnimals] = React.useState(true);

  const trendChartData = React.useMemo(
    () =>
      [...trendData]
        .sort((a, b) => {
          const yearDiff = (a.year ?? yearStart) - (b.year ?? yearStart);
          return yearDiff !== 0 ? yearDiff : a.weekNumber - b.weekNumber;
        })
        .map((item) => ({
          ...item,
          liveCount: (item.animalCount ?? 0) - (item.diedAnimalCount ?? 0),
          xLabel: isMultiYear && item.year != null ? `${item.year}-W${item.weekNumber}` : String(item.weekNumber)
        })),
    [trendData, isMultiYear, yearStart]
  );

  const trendChartConfig = React.useMemo<ChartConfig>(
    () => ({
      ...(showDiedAnimals
        ? { liveCount: { label: t('f-strategy-trend-treated-num'), color: '#413ea0' } }
        : { animalCount: { label: t('f-strategy-trend-treated-num'), color: '#413ea0' } }),
      ...(showDiedAnimals && {
        diedAnimalCount: {
          label: t('f-trend-died-animals'),
          color: '#dc2626'
        }
      })
    }),
    [showDiedAnimals, t]
  );

  // filters
  const [selectedMedicalSupply, setSelectedMedicalSupply] = React.useState(null as MedicalSupplyViewModel | null);
  const [isPregnant, setIsPregnant] = React.useState(false);
  const [isLactating, setIsLactating] = React.useState(false);

  // diseases for selected medical supply
  const [allDiseases, setAllDiseases] = React.useState<HealthSystemDomainDiseasesDiseaseViewModel[]>([]);

  React.useEffect(() => {
    apiService.api.diseases
      .diseasesGetAllDiseases({})
      .then((result) => setAllDiseases(result.data))
      .catch(() => {});
  }, []);

  const relatedDiseases = React.useMemo(
    () =>
      selectedMedicalSupply
        ? allDiseases.filter((d) => d.medicalSupply.medicalSupplyId === selectedMedicalSupply.id)
        : [],
    [allDiseases, selectedMedicalSupply]
  );

  const defaultWeekRange = getDefaultWeekRange();
  const [weekStart, setWeekStart] = React.useState<number>(weekStartProp ?? defaultWeekRange.start);
  const [weekEnd, setWeekEnd] = React.useState<number>(weekEndProp ?? defaultWeekRange.end);

  // drill-down state
  const [selectedWeek, setSelectedWeek] = React.useState<number | null>(null);
  const [selectedWeekYear, setSelectedWeekYear] = React.useState<number>(yearEnd);
  const [ageGrouping, setAgeGrouping] = React.useState<'weeks' | 'days'>('weeks');
  const [ageTrendData, setAgeTrendData] = React.useState<AnimalAgeTrendDataInfo[]>([]);
  const [isAgeBusy, setIsAgeBusy] = React.useState(false);

  const reloadTrendData = () => {
    setLoaded(false);
  };

  const loadTrendData = async () => {
    setIsBusy(true);

    injectionService
      .getAnimalInjectionTrendData(
        animalType,
        yearStart,
        yearEnd,
        selectedMedicalSupply?.id,
        isPregnant,
        isLactating,
        weekStart,
        weekEnd
      )
      .then((result) => {
        setTrendData(result.trendData);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const loadAgeTrendData = async (weekNumber: number, year: number) => {
    setIsAgeBusy(true);

    injectionService
      .getAnimalInjectionAgeTrendData(animalType, year, weekNumber, selectedMedicalSupply?.id, isPregnant, isLactating)
      .then((result) => {
        setAgeTrendData(result.trendData);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsAgeBusy(false));
  };

  React.useEffect(() => {
    if (!loaded) {
      const load = async () => loadTrendData();
      load();
    }
  }, [loaded]);

  React.useEffect(() => {
    if (loaded) {
      setLoaded(false);
      setSelectedWeek(null);
    }
  }, [animalType]);

  const handleMedicalSupplyChange = (supply: MedicalSupplyViewModel | null) => {
    setSelectedMedicalSupply(supply);
    setLoaded(false);
    setSelectedWeek(null);
  };

  const handleBarClick = (data: any) => {
    const payload = data?.activePayload?.[0]?.payload;
    if (payload?.weekNumber && payload?.animalCount > 0) {
      const clickedYear: number = payload.year ?? yearEnd;
      setSelectedWeek(payload.weekNumber);
      setSelectedWeekYear(clickedYear);
      loadAgeTrendData(payload.weekNumber, clickedYear);
    }
  };

  const handleBackToTrend = () => {
    setSelectedWeek(null);
    setAgeTrendData([]);
  };

  const ageChartData = React.useMemo(() => {
    const grouped: Record<number, { liveCount: number; diedCount: number }> = {};
    for (const item of ageTrendData) {
      const key = ageGrouping === 'weeks' ? item.ageInWeeks : item.ageInDays;
      if (!grouped[key]) grouped[key] = { liveCount: 0, diedCount: 0 };
      if (item.isDead) {
        grouped[key].diedCount += 1;
      } else {
        grouped[key].liveCount += 1;
      }
    }
    return Object.entries(grouped)
      .map(([age, { liveCount, diedCount }]) => ({
        age: Number(age),
        count: showDiedAnimals ? liveCount : liveCount + diedCount,
        diedCount,
        totalCount: liveCount + diedCount
      }))
      .sort((a, b) => a.age - b.age);
  }, [ageTrendData, ageGrouping, showDiedAnimals]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zm5.6 8H19v6h-2.8v-6z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-800">{t('f-strategy-trend')}</h2>
        </div>
        <ReloadIconButton action={() => reloadTrendData()} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="min-w-50">
          <AnimalTypeTabs value={animalType} handleTabChange={handleTabChange} />
        </div>
        <div className="min-w-50">
          <MedicalSupplySelectModal
            fullWidth={true}
            supply={selectedMedicalSupply}
            onChange={handleMedicalSupplyChange}
          />
        </div>
        <div className="flex items-center gap-2">
          {/* From: year + week */}
          <TextField
            variant="outlined"
            type="number"
            label={t('f-com-year')}
            size="small"
            value={yearStart}
            style={{ width: 90 }}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= currentYear - 20 && v <= currentYear) {
                setYearStart(v);
                if (v > yearEnd) setYearEnd(v);
                setLoaded(false);
                setSelectedWeek(null);
              }
            }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel id="week-from-label">{t('f-com-week-from')}</InputLabel>
            <Select
              {...weekMenuProps}
              labelId="week-from-label"
              value={weekStart}
              onChange={(e) => {
                const v = Number(e.target.value);
                setWeekStart(v);
                if (v > weekEnd) setWeekEnd(v);
                setLoaded(false);
                setSelectedWeek(null);
              }}
              label={t('f-com-week-from')}
            >
              {allWeeks.map((w) => (
                <MenuItem key={w} value={w} disabled={yearStart >= currentYear && w > currentWeek}>
                  {`${t('f-com-week')} ${w}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <span className="text-gray-400 text-sm">–</span>

          {/* To: year + week */}
          <TextField
            variant="outlined"
            type="number"
            label={t('f-com-year')}
            size="small"
            value={yearEnd}
            style={{ width: 90 }}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= currentYear - 20 && v <= currentYear) {
                setYearEnd(v);
                if (v < yearStart) setYearStart(v);
                setLoaded(false);
                setSelectedWeek(null);
              }
            }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel id="week-to-label">{t('f-com-week-to')}</InputLabel>
            <Select
              {...weekMenuProps}
              labelId="week-to-label"
              value={weekEnd}
              onChange={(e) => {
                const v = Number(e.target.value);
                setWeekEnd(v);
                if (v < weekStart) setWeekStart(v);
                setLoaded(false);
                setSelectedWeek(null);
              }}
              label={t('f-com-week-to')}
            >
              {allWeeks.map((w) => (
                <MenuItem key={w} value={w} disabled={yearEnd >= currentYear && w > currentWeek}>
                  {`${t('f-com-week')} ${w}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <FormControlLabel
          className="ml-0!"
          control={
            <Checkbox
              disabled={!isSowOrGilt}
              checked={isPregnant}
              onChange={(e) => {
                setIsPregnant(e.target.checked);
                setLoaded(false);
                setSelectedWeek(null);
              }}
              value="isPregnant"
              color="primary"
            />
          }
          label={t('f-animal-is-preg')}
        />
        <FormControlLabel
          className="ml-0!"
          control={
            <Checkbox
              disabled={!isSowOrGilt}
              checked={isLactating}
              onChange={(e) => {
                setIsLactating(e.target.checked);
                setLoaded(false);
                setSelectedWeek(null);
              }}
              value="isLactating"
              color="primary"
            />
          }
          label={t('f-animal-is-lact')}
        />
        <FormControlLabel
          className="ml-0!"
          control={
            <Checkbox
              checked={showDiedAnimals}
              onChange={(e) => setShowDiedAnimals(e.target.checked)}
              value="showDiedAnimals"
              color="secondary"
            />
          }
          label={t('f-trend-show-died-animals')}
        />
      </div>

      {/* Related diseases for selected medical supply */}
      {relatedDiseases.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-500">{t('f-com-diseases')}:</span>
          <DiseaseInfo diseases={relatedDiseases} />
        </div>
      )}

      {isBusy && (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="p-6">
              <div className="flex items-end gap-3 h-62.5">
                {[55, 75, 45, 85, 65, 90, 50, 70, 80, 60, 40, 72].map((h, i) => (
                  <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart area */}
      {loaded && !isBusy && (
        <Card>
          <CardHeader className="pb-2 min-h-13">
            {selectedWeek !== null ? (
              <div className="flex items-center justify-between">
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbLink onClick={handleBackToTrend}>{t('f-strategy-trend')}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>
                        Week {selectedWeek}
                        {(isMultiYear || selectedWeekYear !== yearEnd) && ` (${selectedWeekYear})`}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
                <ToggleGroup
                  type="single"
                  value={ageGrouping}
                  onValueChange={(v) => {
                    if (v) setAgeGrouping(v as 'weeks' | 'days');
                  }}
                  variant="outline"
                  size="sm"
                >
                  <ToggleGroupItem value="weeks">{t('f-com-weeks')}</ToggleGroupItem>
                  <ToggleGroupItem value="days">{t('f-com-days')}</ToggleGroupItem>
                </ToggleGroup>
              </div>
            ) : (
              <h3 className="text-sm font-medium text-gray-700">{t('f-strategy-trend')}</h3>
            )}
          </CardHeader>
          <CardContent>
            <div className="w-full aspect-2/1 min-h-62.5 max-h-100">
              {selectedWeek !== null ? (
                // Drill-down: Age distribution chart
                <>
                  {isAgeBusy && (
                    <div className="flex items-end gap-3 h-full">
                      {[55, 75, 45, 85, 65, 90, 50, 70, 80, 60, 40, 72].map((h, i) => (
                        <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  )}
                  {!isAgeBusy && (
                    <ChartContainer
                      config={{
                        count: { label: t('f-strategy-trend-treated-num'), color: '#6366f1' },
                        ...(showDiedAnimals && { diedCount: { label: t('f-trend-died-animals'), color: '#dc2626' } })
                      }}
                      className="w-full h-full"
                    >
                      <BarChart data={ageChartData} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis
                          dataKey="age"
                          tick={{ fontSize: 13, fill: '#6b7280' }}
                          axisLine={false}
                          tickLine={false}
                          label={{
                            value: ageGrouping === 'weeks' ? t('f-com-age-weeks') : t('m-animal-age-days'),
                            position: 'insideBottom',
                            offset: -10,
                            style: { fontSize: 12, fill: '#6b7280' }
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 13, fill: '#6b7280' }}
                          axisLine={false}
                          tickLine={false}
                          width={40}
                          allowDecimals={false}
                        />
                        <RechartsTooltip cursor={false} content={<AgeTooltip grouping={ageGrouping} />} />
                        <ChartLegend content={<ChartLegendContent />} />
                        <Bar
                          name={t('f-strategy-trend-treated-num')}
                          dataKey="count"
                          stackId="a"
                          fill="var(--color-count)"
                          radius={showDiedAnimals ? [0, 0, 4, 4] : [4, 4, 4, 4]}
                          barSize={24}
                        />
                        {showDiedAnimals && (
                          <Bar
                            name={t('f-trend-died-animals')}
                            dataKey="diedCount"
                            stackId="a"
                            fill="var(--color-diedCount)"
                            radius={[4, 4, 0, 0]}
                            barSize={24}
                          />
                        )}
                      </BarChart>
                    </ChartContainer>
                  )}
                </>
              ) : (
                // Weekly trend  chart (stacked bar)
                <ChartContainer config={trendChartConfig} className="w-full h-full">
                  <ComposedChart data={trendChartData} onClick={handleBarClick} className="cursor-pointer">
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="xLabel"
                      padding={{ left: 30, right: 30 }}
                      scale="band"
                      tickLine={false}
                      axisLine={false}
                      height={isMultiYear ? 44 : 30}
                      tick={(props: any) => <TrendXTick {...props} data={trendChartData} multiYear={isMultiYear} />}
                    />
                    <YAxis axisLine={false} tickLine={false} width={40} />
                    <RechartsTooltip content={<TrendTooltip t={t} multiYear={isMultiYear} />} cursor={false} />
                    <ChartLegend content={<ChartLegendContent />} />
                    {isMultiYear &&
                      trendChartData
                        .filter((item, i) => i > 0 && item.year !== trendChartData[i - 1].year)
                        .map((item) => (
                          <ReferenceLine
                            key={`yr-sep-${item.year}`}
                            x={item.xLabel}
                            stroke="#9ca3af"
                            strokeWidth={1.5}
                            strokeDasharray="4 3"
                            ifOverflow="visible"
                          />
                        ))}
                    <Bar
                      name={t('f-strategy-trend-treated-num')}
                      dataKey={showDiedAnimals ? 'liveCount' : 'animalCount'}
                      stackId="a"
                      fill={showDiedAnimals ? 'var(--color-liveCount)' : 'var(--color-animalCount)'}
                      style={{ cursor: 'pointer' }}
                      shape={(props: any) => {
                        const hasDied = showDiedAnimals && props.payload?.diedAnimalCount > 0;
                        return <Rectangle {...props} radius={hasDied ? [0, 0, 4, 4] : [4, 4, 4, 4]} />;
                      }}
                    />
                    {showDiedAnimals && (
                      <Bar
                        name={t('f-trend-died-animals')}
                        dataKey="diedAnimalCount"
                        stackId="a"
                        fill="var(--color-diedAnimalCount)"
                        radius={[4, 4, 0, 0]}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                    <Line
                      name=" "
                      type="monotone"
                      dataKey="animalCount"
                      stroke={colors.barChartLine}
                      dot={false}
                      legendType="none"
                    />
                  </ComposedChart>
                </ChartContainer>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
